<template>
  <SogPage
    id="page"
    :title="_title"
  >
    <SogCard>
      <SogCardActions
        v-show="_accountStore.isRoot&&_countNotLinked>0"
        :is-toolbar="true"
      >
        <SogBadge
          v-show="_accountStore.isRoot"
          :value="_countNotLinked"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-link-variant"
            tooltip="Formularnummer zuweisen"
            @click="onAddLink"
          >
            zuweisen
          </SogCardActionButton>
        </SogBadge>
        <SogCardActionButton
          :kind="SogButtonKind.Back"
        />
      </SogCardActions>
      <SogCardContent v-show="_progressInfo">
        <SogProgressInfoComponent :progress-info="_progressInfo" />
      </SogCardContent>
      <SogCardContent v-show="(_accountStore.isRoot)&&_countNotLinked>0">
        <SogForm>
          <SogFlexRow :align-x="SogAlign.Start">
            <SogFormField>
              <SogNumericTextBox
                v-model="_countNotLinked"
                placeholder="Anzahl"
                format="n"
                width="100px"
                :min="1"
                :max="_insurances.length-_startIndex"
              />
            </SogFormField>
            <SogFormField>
              <SogTextBox
                v-model="_prefix"
                placeholder="Prefix"
                width="80px"
              />
            </SogFormField>
            <SogFormField>
              <SogNumericTextBox
                v-model="_startNumber"
                placeholder="Startnummer"
                format="n"
                width="150px"
                :min="1"
              />
            </SogFormField>
            <SogFormField>
              <SogNumericTextBox
                v-model="_digitCount"
                placeholder="Anzahl Ziffern"
                format="n"
                width="100px"
                :min="1"
              />
            </SogFormField>
          </SogFlexRow>
          <SogFlexRow
            :align-x="SogAlign.Start"
            :align-y="SogAlign.Center"
          >
            <div style="margin-left: 10px">
              {{ _sample }}
            </div>
          </SogFlexRow>
        </SogForm>
      </SogCardContent>
      <SogCardActions
        v-show="(_accountStore.isRoot||_accountStore.hasExportBlankProduct)&&_countLinked>0"
        :is-toolbar="true"
      >
        <SogBadge
          v-show="_accountStore.isRoot&&_countLinked>0"
          :value="_countLinked"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionDropDownButton
            mdi-icon="mdi-printer"
            :items="_menuPrintExportBlank"
            tooltip="Policy ausdrücken"
            @select="onPrintExportBlank"
          />
        </SogBadge>
        <SogBadge
          v-show="_accountStore.hasExportBlankProduct&&_countLinked>0"
          :value="_countLinked"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-printer"
            tooltip="Policy ausdrücken"
            @click="onPrintExportBlank(undefined)"
          />
        </SogBadge>
        <SogBadge
          v-show="_accountStore.isRoot&&_countLinked>0"
          :value="_countLinked"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-link-variant-remove"
            tooltip="Zuweisung löschen"
            @click="onRemoveLink"
          >
            löschen
          </SogCardActionButton>
        </SogBadge>
        <SogCardActionButton
          :kind="SogButtonKind.Back"
        />
      </SogCardActions>
      <SogCardContent>
        <SogGrid
          ref="_grid"
          :columns="_columns"
          :data="_insurances"
          :allow-paging="false"
          @selected="onSelected"
        />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {onMounted, reactive, ref, watchEffect} from "vue";
import {onBeforeRouteUpdate, useRouter} from "vue-router";
import {useSpinner} from "@/shared/composables/SogSpinner";
import SogGrid from "@/shared/ui/SogGrid.vue";
import Insurance, {sortInsuranceByNumber} from "@/shared/model/Insurance";
import {ColumnModel} from "@syncfusion/ej2-grids/src/grid/models/column";
import {DefaultColumn} from "@/shared/ui/types/SogGridColumnDefinitions";
import {useInsuranceStore} from "@/modules/Insurance/store";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogForm from "@/shared/ui/SogForm.vue";
import SogFormField from "@/shared/ui/SogFormField.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogNumericTextBox from "@/shared/ui/SogNumericTextBox.vue";
import SogFlexRow from "@/shared/ui/SogFlexRow.vue";
import SogBadge from "@/shared/ui/SogBadge.vue";
import {useAccountStore} from "@/stores/accountStore";
import {computed} from "vue";
import {fillNumberWithZeros} from "@/shared/utils/NumberUtils";
import {SogAlign} from "@/shared/ui/types/SogAlign";
import apiInsurance from "@/modules/Insurance/api";
import progressService from "@/shared/services/ProgressService";
import {ProgressInfo} from "@/shared/model/ProgressInfo";
import notificationService from "@/shared/services/NotificationService";
import SogProgressInfoComponent from "@/shared/ui/SogProgressInfoComponent.vue";
import apiFile from "@/shared/services/apiFile";
import {compareStrings} from "@/shared/utils/StringUtils";
import {storeToRefs} from "pinia";
import SogDropDownButtonItem from "@/shared/ui/types/SogDropDownButtonItem";
import SogCardActionDropDownButton from "@/shared/ui/SogCardActionDropDownButton.vue";


const _title = ref("Ausfuhrversicherungskarten")
const _spinner = useSpinner()
const _accountStore = useAccountStore();

const _insurances = reactive<Insurance[]>([])
const _columns: ColumnModel[] = [
  {...DefaultColumn, field: 'id', headerText: 'id', isPrimaryKey: true, visible: false},
  {...DefaultColumn, field: 'insuranceNumber', headerText: 'Versicherungsnummer'},
  {...DefaultColumn, field: 'formNumber', headerText: 'Formularnummer'},
]

const _grid = ref<InstanceType<typeof SogGrid> | null>(null)

const _insuranceStore = useInsuranceStore()
const _prefix = ref("AV")
const _startNumber = ref(1)
const _digitCount = ref(7)
/*
const _countNotLinked = ref(1)
const _countLinked = ref(1)
*/
const _startIndex = ref(0)
const _progressInfo = ref<ProgressInfo | undefined>(undefined)
const _router = useRouter();
const {printExportInsurancePage: _printExportInsurancePage} = storeToRefs(_insuranceStore)
const _menuPrintExportBlank: SogDropDownButtonItem[] = [
  {id: "current", text: (new Date().getFullYear()).toString()},
  {separator: true},
  {id: "next", text: (new Date().getFullYear() + 1).toString()},
  /*
    {id: "none", text: "Ohne"}
  */
];


const _sample = computed(() => {
  const t_start = fillNumberWithZeros(_startNumber.value, _digitCount.value)
  const t_end = fillNumberWithZeros((_startNumber.value + _countNotLinked.value - 1), _digitCount.value)
  return `${_insurances[_startIndex.value]?.insuranceNumber} (${_prefix.value}${t_start}) - ${_insurances[_startIndex.value + _countNotLinked.value - 1]?.insuranceNumber} (${_prefix.value}${t_end})`
})

async function p_init() {
  if (!(_accountStore.isRoot||_accountStore.hasExportBlankProduct)) {
    _router.back()
  }
  _spinner.show()
  try {
    _title.value = "Ausfuhrversicherungskarten"
    _insurances.length = 0;
    _insurances.push(...sortInsuranceByNumber(_printExportInsurancePage.value.insurances))
    _startIndex.value = 0;
    _grid?.value?.refresh();
  } finally {
    _spinner.hide()
  }
}

onMounted(async () => {
  await p_init()
})

onBeforeRouteUpdate(async () => {
  await p_init()
});


/*watchEffect(() => {
  //console.log("watchEffect")

  const t_count = _insurances.length - _startIndex.value;


  _countNotLinked.value = _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => !t.formNumber).length;


  _countLinked.value = _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => t.formNumber).length
  /!*
    console.log(_countLinked.value)
    console.log(_countNotLinked.value)
  *!/

});*/

const _countNotLinked = computed(() => {
  const t_count = _insurances.length - _startIndex.value;
  return _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => !t.formNumber).length;
});

const _countLinked = computed(() => {
  const t_count = _insurances.length - _startIndex.value;
  return _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => t.formNumber).length;
});

function onSelected(args: any) {
  _startIndex.value = args ? _insurances.indexOf(args) : 0;
}

async function onAddLink() {
  _spinner.show()
  const t_count = _insurances.length - _startIndex.value;
  const array = _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => !t.formNumber).map(t => t.id ?? "");
  const manufacturerId = _insurances[0].manufacturerId;
  const progressId = await apiInsurance.insuranceBlankLink(array, _prefix.value, _startNumber.value, _digitCount.value, manufacturerId);
  if (progressId) {
    await progressService.addToGroup([progressId], false, onProgressRefresh)
  }
}

async function onProgressRefresh(group: string, progressInfo: ProgressInfo) {
  _progressInfo.value = progressInfo
  if (progressInfo) {
    if (progressInfo.finishDate) {
      await progressService.removeFromGroup([progressInfo.groupId ?? ""], onProgressRefresh)
      if (!progressInfo.successful) {
        notificationService.error(progressInfo.resultMessage ?? "")
      } else {
        if (progressInfo.successful) {
//          const array = _insurances.slice(_startIndex.value, _startIndex.value + _countNotLinked.value).map(t => t.id ?? "");
          const array = _insurances.map(t => t.id ?? "");
          const res = await apiInsurance.list(array);
          if (res) {
            _insurances.length = 0;
            _insurances.push(...res.sort((a, b) => {
              return compareStrings(a.insuranceNumber, b.insuranceNumber);
            }))
            _startIndex.value = 0;
            _grid?.value?.refresh();
          }
          notificationService.info("Ok")
        }
      }
      _progressInfo.value = undefined
      _spinner.hide()

    }
  }
}

async function onRemoveLink() {
  _spinner.show()
  const t_count = _insurances.length - _startIndex.value;
  const array = _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => t.formNumber).map(t => t.id ?? "");
  const progressId = await apiInsurance.insuranceBlankLinkRemove(array);
  if (progressId) {
    await progressService.addToGroup([progressId], false, onProgressRefresh)
  }
  _spinner.hide()
}


async function onPrintExportBlank(item?: SogDropDownButtonItem) {
  try {
    _spinner.show()
    const t_currentYear = item?.id === "current" ? true : item?.id === "next" ? false : undefined;
    const t_count = _insurances.length - _startIndex.value;
    const array = _insurances.slice(_startIndex.value, _startIndex.value + t_count).filter(t => t.formNumber).map(t => t.id ?? "");
    const t_id = await apiInsurance.initPdf(array, true, 5, 0, t_currentYear);
    if (t_id) {
      await apiFile.downloadFile(t_id)
    }
    _spinner.hide()

  } finally {
    _spinner.hide()
  }

}

</script>
<style scoped>
</style>
